import { Injectable, signal } from '@angular/core';

/**
 * Handles the application theme switcher (Dark and Light modes).
 * Stores the user theme preference in localStorage and applies/removes
 * the `.light-theme` class on the `document.documentElement`.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly isDarkTheme = signal(true);

  /** A read-only signal indicating if dark theme is active. */
  readonly isDark = this.isDarkTheme.asReadonly();

  constructor() {
    this.initTheme();
  }

  private initTheme(): void {
    const savedTheme = typeof localStorage !== 'undefined' ? localStorage.getItem('theme') : null;
    const prefersDark =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : true;

    const useDark = savedTheme ? savedTheme === 'dark' : prefersDark;
    this.isDarkTheme.set(useDark);
    this.applyTheme(useDark);
  }

  /** Toggles between dark and light themes. */
  toggleTheme(): void {
    const nextDark = !this.isDarkTheme();
    this.isDarkTheme.set(nextDark);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('theme', nextDark ? 'dark' : 'light');
    }

    this.applyTheme(nextDark);
  }

  private applyTheme(dark: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    const html = document.documentElement;
    if (dark) {
      html.classList.remove('light-theme');
    } else {
      html.classList.add('light-theme');
    }
  }
}
